import { FingerprintIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { encryptsGarages, hasPasskeys, safeNext, signInUrl, useMe } from "@/lib/account";
import { errorMessage } from "@/lib/errors";
import { useLocation } from "@/lib/pages";
import { useTRPCClient } from "@/lib/trpc";
import { AuthCard } from "./account-app";
import { createPasskey, deviceName, passkeyCancelled, passkeyPrf, takePrf } from "./passkeys";
import { FormError } from "./sign-in";

/**
 * Adds a passkey for the signed-in person: the server's options, the browser's dialog, the
 * server's check. Through the client, not mutations, so the pages around it don't reload `me`
 * halfway. Resolves false when the person closed the browser's dialog.
 */
export function useAddPasskey() {
  const { t } = useTranslation();
  const client = useTRPCClient();
  return async (name?: string): Promise<boolean> => {
    const options = await client.account.passkeys.registerOptions.mutate();
    let response: Awaited<ReturnType<typeof createPasskey>>;
    try {
      response = await createPasskey(options);
    } catch (error) {
      if (passkeyCancelled(error)) return false;
      throw error;
    }
    // Its PRF output, where the browser gives one, opens the person's key (ADR 0022).
    let prf = takePrf(response);
    await client.account.passkeys.register.mutate({
      response: { ...response },
      name: name?.trim() || deviceName() || t("account.passkeyDefaultName"),
    });
    if (encryptsGarages()) {
      try {
        prf ??= await passkeyPrf(response.id);
        if (prf) await client.keys.addPasskey.mutate({ credentialId: response.id, prf });
      } catch {
        // Without PRF the passkey still signs in; the password then opens the key.
      }
    }
    return true;
  };
}

/**
 * The one-time offer to add a passkey (ADR 0011): "Add passkey" or "Not now", either of which
 * answers it for good. `onAnswered` goes on.
 */
export function PasskeyOffer({
  onAnswered,
  back,
}: {
  onAnswered: () => Promise<void> | void;
  back?: () => void;
}) {
  const { t } = useTranslation();
  const client = useTRPCClient();
  const add = useAddPasskey();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (work: () => Promise<boolean>) => {
    setError(null);
    setPending(true);
    try {
      if (await work()) await onAnswered();
      else setPending(false);
    } catch (e) {
      setError(errorMessage(e, t));
      setPending(false);
    }
  };
  return (
    <div className="flex flex-col gap-5">
      <FormError message={error} />
      <div className="flex flex-col gap-2">
        <Button size="lg" disabled={pending} onClick={() => run(() => add())}>
          <FingerprintIcon />
          {t("account.passkeyAdd")}
        </Button>
        <div className="flex gap-2">
          {back ? (
            <Button variant="outline" size="lg" disabled={pending} onClick={back}>
              {t("welcome.back")}
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="lg"
            className="flex-1"
            disabled={pending}
            onClick={() =>
              run(async () => {
                await client.account.passkeys.dismissOffer.mutate();
                return true;
              })
            }
          >
            {t("account.passkeyNotNow")}
          </Button>
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground">{t("account.passkeyLater")}</p>
    </div>
  );
}

/** /account/passkey: the offer after a sign-in with the password, then on to ?next=. */
export function PasskeyOfferPage() {
  const { t } = useTranslation();
  const { search } = useLocation();
  const next = safeNext(search.get("next"));
  const { data: me, isPending } = useMe();
  // Decided when the page opens, so answering doesn't take it away underneath.
  const [shown, setShown] = useState<boolean | null>(null);
  useEffect(() => {
    if (isPending || shown !== null) return;
    if (!me) window.location.replace(signInUrl(next));
    else if (me.status !== "active" || !me.passkeyOffer || !hasPasskeys()) {
      window.location.replace(next);
    } else setShown(true);
  }, [me, isPending, shown, next]);
  if (!shown) return null;
  return (
    <AuthCard title={t("account.passkeyOfferTitle")} body={t("account.passkeyOfferBody")}>
      <PasskeyOffer onAnswered={() => window.location.assign(next)} />
    </AuthCard>
  );
}
